import React, { useCallback, useRef, useState } from 'react';

import Cropper from 'react-cropper';

import Button from '~/components/Button';
import Modal from '~/components/Modal';
import { wrapDialog } from '~/helpers/dialog-helper';
import { extractRequestError } from '~/helpers/error-helper';
import { base64toFile } from '~/helpers/file-helper';
import useAsync from '~/hooks/use-async';
import 'cropperjs/dist/cropper.css';
import useDidMountAndUpdate from '~/hooks/use-did-mount-and-update';

import styles from './PictureModal.module.scss';
import WebcamPicture from './WebcamPicture';

const PictureModal = ({
    personId, onCancel, onChangePicture, ...others
}) => {
    const [photo, setPhoto] = useState(null);
    const [croppedPhoto, setCroppedPhoto] = useState(null);
    const $cropperRef = useRef(null);
    const $hasResized = useRef(false);

    const { call: handleSavePhoto, loading } = useAsync(async () => {
        try {
            const pictureFile = await base64toFile(croppedPhoto);
            onChangePicture(pictureFile);
            onCancel();
        } catch (err) {
            extractRequestError(
                err,
                'Não foi possível salvar a foto. Por favor, tente novamente.',
            );
        }
    }, [croppedPhoto, onChangePicture, onCancel]);

    const onCrop = useCallback(() => {
        const cropper = $cropperRef.current?.cropper;
        setCroppedPhoto(cropper.getCroppedCanvas().toDataURL('image/png'));
    }, []);

    const handleRetakePhoto = useCallback(() => {
        setPhoto(null);
        setCroppedPhoto(null);
    }, []);

    const cropperRef = useCallback(ref => {
        $cropperRef.current = ref;

        const { cropper } = ref;

        if (cropper && !$hasResized.current) {
            cropper.setData({
                width: 2000,
                height: 2000,
            });

            $hasResized.current = true;
        }
    }, []);

    useDidMountAndUpdate(() => {
        if (!photo) {
            $hasResized.current = false;
        }
    }, [photo]);

    const renderPhoto = () => {
        if (!photo) {
            return (
                <WebcamPicture
                    setPhoto={setPhoto}
                />
            );
        }

        return (
            <div className={styles.container}>
                <Cropper
                    src={photo}
                    viewMode={1}
                    crop={onCrop}
                    guides={false}
                    center={false}
                    rotatable={false}
                    ref={cropperRef}
                    background={false}
                    toggleDragModeOnDblclick={false}
                />
                <div className={styles.actions}>
                    <Button
                        onClick={handleSavePhoto}
                        disabled={!photo}
                        loading={loading}
                    >
                        Salvar
                    </Button>
                    <Button
                        onClick={handleRetakePhoto}
                        type="primary"
                        backgroundColor="#FFF"
                        fontColor="#000"
                        className={styles.cancelButton}
                        loading={loading}
                    >
                        Tirar outra
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Modal
            {...others}
            onCancel={onCancel}
            width={700}
            footer={null}
        >
            {renderPhoto()}
        </Modal>
    );
};

export default wrapDialog(PictureModal);
